import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { countries, Country } from "@/data/countries";
import { cn } from "@/lib/utils";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: string;
  onCountryChange: (code: string) => void;
  disabled?: boolean;
}

export const PhoneInput = ({
  value,
  onChange,
  countryCode,
  onCountryChange,
  disabled,
}: PhoneInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCountry = countries.find((c) => c.code === countryCode) || countries.find((c) => c.code === "FR")!;

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectCountry = (country: Country) => {
    onCountryChange(country.code);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex">
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-1 px-3 rounded-r-none border-r-0 bg-white/5 border-white/20 hover:bg-white/10"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span className="text-lg">{selectedCountry.flag}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
        <Input
          ref={inputRef}
          type="tel"
          placeholder={`${selectedCountry.dialCode} 6 12 34 56 78`}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9\s]/g, ""))}
          className="rounded-l-none flex-1"
          disabled={disabled}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-72 max-h-64 overflow-auto rounded-lg border border-border bg-card shadow-lg">
          <div className="sticky top-0 bg-card p-2 border-b border-border">
            <Input
              type="text"
              placeholder="Rechercher un pays..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
              autoFocus
            />
          </div>
          <div className="py-1">
            {filteredCountries.map((country) => (
              <button
                key={country.code}
                type="button"
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-accent/50 transition-colors",
                  country.code === countryCode && "bg-accent"
                )}
                onClick={() => handleSelectCountry(country)}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 truncate text-sm">{country.name}</span>
                <span className="text-sm text-muted-foreground">{country.dialCode}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
